import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '@/store/useStore';
import StompConnector from '@/controllers/StompConnector';
import starIcon from '@/assets/icons/star.svg';
import { Button, Icon } from '@/ui/components/base';
import { useBetOnRacer } from '@/ui/hooks/useBetOnRacer';
import { cn, formatCurrency, truncate } from '@/ui/lib/utils';
import { BetSelector } from './BetSelector/BetSelector';
import { allGameRoundsQuery } from '@/ui/api/connector/queries';
import { getConnectionData } from '@/helpers/url';
import './bottomBar.css';

export const BottomBar = () => {
    const bet = useStore((state) => state.bet);
    const multiplier = useStore((state) => state.multiplier);
    const currency = useStore((state) => state.currency);
    const selectedRacer = useStore((state) => state.selectedRacer);
    const selectedRacerDetails = useStore((state) => state.getSelectedRacerDetails());
    const raceId = useStore((state) => state.raceId);
    const betPlaced = useStore((state) => state.betPlaced);
    const [placingBet, setPlacingBet] = useState(false);
    const gameState = useStore((state) => state.gameState);
    const [openBetSelector, setOpenBetSelector] = useState(false);
    const betOnRacer = useBetOnRacer();
    const won = betOnRacer?.position === '1st' && gameState === 'CELEBRATION';
    const bettingOpen = gameState === 'PLACE_BET';
    const canPlaceBet = bettingOpen && !betPlaced && !placingBet;
    const possibleWin = bet * multiplier * (selectedRacerDetails?.multiplier ?? 0);
    const query = useQueryClient();
    const userRef = useStore((state) => state.userRef);

    const placeBetButtonLabel = useMemo(() => {
        if (betPlaced) return 'Bet placed';
        if (!bettingOpen) return 'Bets closed';
        if (placingBet) return 'Placing bet...';
        return 'Place bet';
    }, [betPlaced, placingBet, bettingOpen]);

    const toggleBetSelector = () => setOpenBetSelector((prev) => !prev);
    const triggerRacerPanelShake = useStore((state) => state.triggerRacerPanelShake);

    const handleBet = () => {
        if (!canPlaceBet) return;
        if (!selectedRacer) {
            triggerRacerPanelShake();
            return;
        }
        StompConnector.performAction(bet * 100, selectedRacer, raceId);
        setPlacingBet(true);
        query.invalidateQueries({
            queryKey: [allGameRoundsQuery(userRef, getConnectionData().gameId, 50, 0).queryKey[0]],
        });
    };

    useEffect(() => {
        if (betPlaced) {
            setPlacingBet(false);
        }
    }, [betPlaced]);

    return (
        <footer className="bottom-bar">
            <div className="bottom-bar__top-block">
                <Button
                    className="bottom-bar__bet-button"
                    variant="secondary"
                    disabled={!canPlaceBet}
                    onClick={toggleBetSelector}
                    rounding="large"
                >
                    Bet {formatCurrency(bet, currency)}
                </Button>
                <Button
                    className="bottom-bar__place-bet-button"
                    variant="primary"
                    disabled={!canPlaceBet}
                    onClick={handleBet}
                >
                    {placeBetButtonLabel}
                </Button>
                <div className="bottom-bar__possible-win">
                    <Icon
                        src={starIcon}
                        className={cn('bottom-bar__star-icon', won && 'bottom-bar__star-icon--win')}
                    />
                    <div className="bottom-bar__possible-win-label">Possible win</div>
                    <div key={possibleWin} className="bottom-bar__possible-win-amount">
                        {selectedRacerDetails && (betPlaced || bettingOpen)
                            ? formatCurrency(truncate(possibleWin), currency)
                            : '--'}
                    </div>
                </div>
            </div>

            <BetSelector isOpen={openBetSelector} onClose={() => setOpenBetSelector(false)} />
        </footer>
    );
};
